import { QueryClient } from "@tanstack/react-query";
import {
  ParamsForMethod,
  QueryDataForMethod,
  QueryMethodForModel,
  ModelName,
  TellescopeQueryKey,
  TellescopeQueryUpdate,
} from "../../types";
import { TellescopeQueryFilters } from "../../hooks";
import { buildTellescopeQueryKey } from "../../keys";

/**
 * Arguments for getting multiple queries' data from cache with optional filtering.
 *
 * @template Model - The Tellescope model name
 * @template Method - The query method for the model
 */
export interface GetTellescopeQueriesDataArgs<
  Model extends ModelName,
  Method extends QueryMethodForModel<Model>,
  TError = Error,
> {
  /** The Tellescope model name */
  model: Model;
  /** Query method name */
  method: Method;
  /** Optional request parameters to narrow down queries. If omitted, returns all queries for the method. */
  request?: Partial<ParamsForMethod<Model, Method>>;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /** Optional query filters to narrow down which queries to return */
  filters?: Omit<
    TellescopeQueryFilters<
      QueryDataForMethod<Model, Method>,
      TError,
      QueryDataForMethod<Model, Method>,
      TellescopeQueryKey<Model, Method>
    >,
    "queryKey"
  >;
  requestKeysToOmitFromQueryKey?: (keyof ParamsForMethod<Model, Method>)[];
}

/**
 * Gets cached data for all matching queries from the query client.
 * Returns an array of [queryKey, data] tuples for all queries that match the provided filters.
 * For infinite queries, data is wrapped in InfiniteData structure.
 *
 * @param args - The get queries data arguments
 * @returns An array of [queryKey, data] tuples
 *
 * @example
 * ```tsx
 * // Get all queries for a method
 * const allGetOneQueries = getTellescopeQueriesData({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 * }); // Returns Array<[TellescopeQueryKey<'users', 'getOne'>, User | undefined]>
 *
 * // Get specific query
 * const userQuery = getTellescopeQueriesData({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 * }); // Returns Array<[TellescopeQueryKey<'users', 'getOne'>, User | undefined]>
 *
 * // With filters
 * const activeQueries = getTellescopeQueriesData({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   filters: {
 *     stale: false,
 *   },
 * });
 * ```
 */
export const getTellescopeQueriesData = <
  Model extends ModelName,
  Method extends QueryMethodForModel<Model>,
  TError = Error,
>({
  model,
  method,
  request,
  queryClient,
  filters,
}: GetTellescopeQueriesDataArgs<Model, Method, TError>): TellescopeQueryUpdate<Model, Method>[] => {
  const queryKey = buildTellescopeQueryKey({ model, method, request });

  // @ts-expect-error Tanstack doesn't expose a generic for the query key
  return queryClient.getQueriesData({
    queryKey,
    ...filters,
  });
};
